import { isStudioModuleEntitled, isStudioModuleTrialAvailable } from "./studioModules";
import { currentKarkhanaAddress, KP } from "../pages/karkhana/karkhanaPaths";
import { DRAFT_KEY_PREFIX, draftScope, flushAllDrafts } from "../pages/karkhana/daily/dailyDraft";

export const isKarkhanaEnabled = (): boolean =>
  process.env.REACT_APP_KARKHANA_ENABLED === "true" ||
  process.env.REACT_APP_ENV === "loc";

const isStudioStaff = (role?: string): boolean =>
  role === "SUPER_ADMIN" ||
  role === "ORGANIZATION_ADMIN" ||
  role === "ORGANIZATION_MANAGER";

export const canUseKarkhana = (role?: string): boolean => {
  if (!isKarkhanaEnabled() || !isStudioStaff(role)) {
    return false;
  }
  if (role === "SUPER_ADMIN" || process.env.REACT_APP_ENV === "loc") {
    return true;
  }
  return isStudioModuleEntitled("KARKHANA");
};

export const canManageKarkhana = (role?: string): boolean => canUseKarkhana(role);

/** Corrections after work is entered (a saree's start date or number): the studio owner or a manager only. */
export const isOwnerOrManager = (role?: string): boolean => role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER";

/** An existing studio that never had Karkhana: opening it starts the free trial. */
export const canStartKarkhanaTrial = (role?: string): boolean =>
  isKarkhanaEnabled() &&
  (role === "ORGANIZATION_ADMIN" || role === "ORGANIZATION_MANAGER") &&
  !canUseKarkhana(role) &&
  isStudioModuleTrialAvailable("KARKHANA");

/** Show Karkhana in the menu: usable now, or the trial is waiting for its first open. */
export const canOpenKarkhana = (role?: string): boolean => canUseKarkhana(role) || canStartKarkhanaTrial(role);

/** Shop-floor supervisor: sees only the daily rounds (K25). The server enforces this too. */
export const KARKHANA_SUPERVISOR = "KARKHANA_SUPERVISOR";

export const isKarkhanaSupervisor = (role?: string): boolean => role === KARKHANA_SUPERVISOR;

/** Pages a supervisor may open; everything else redirects to the rounds. */
export const SUPERVISOR_PATHS = [KP.rounds, KP.dailyWork, KP.attendance, KP.pay, KP.paySheets, KP.cashBook, KP.jobWork, KP.sarees, "/karkhana/looms", KP.approvals, KP.more,
  // A saree's HKP label: the server shows supervisors the no-price card, so a camera scan can return there after sign-in.
  "/q"];

/** Karkhana home (the loom board) is theirs too, but not everything under it. */
const SUPERVISOR_EXACT = ["/karkhana", "/karkhana/"];

export const supervisorMayOpen = (address?: string): boolean => {
  // Old addresses (/karkhana/rounds…) are judged by the page they now lead to.
  const path = address ? currentKarkhanaAddress(address) : "";
  return (
    SUPERVISOR_EXACT.includes(path) ||
    SUPERVISOR_PATHS.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`) || path.startsWith(`${p}#`))
  );
};

/** The rounds and approvals screens: studio staff with Karkhana, or a supervisor. */
export const canUseRounds = (role?: string): boolean => {
  if (isKarkhanaSupervisor(role)) {
    return isKarkhanaEnabled() && (process.env.REACT_APP_ENV === "loc" || isStudioModuleEntitled("KARKHANA"));
  }
  return canUseKarkhana(role);
};

/** This phone's Karkhana settings that outlive a logout: language, remembered supervisor mobile, last place worked. */
const DEVICE_KEYS = ["hkp.karkhana.lang", "hkp.karkhana.loginMobile", "hkp.lastArea"];

/**
 * Logout: clears storage but this phone's settings, and the unsaved Daily work of anyone else who
 * used this phone (kept per studio + user). The signed-in user's own copies go: they were asked first.
 */
export const clearKeepingDevice = () => {
  const scope = draftScope();
  const mine = scope ? `${DRAFT_KEY_PREFIX}${scope}.` : null;
  clearStorageKeeping([...DEVICE_KEYS, ...draftKeys().filter((k) => !mine || !k.startsWith(mine))]);
};

const draftKeys = (): string[] => {
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(DRAFT_KEY_PREFIX)) out.push(k);
  }
  return out;
};

const clearStorageKeeping = (keys: string[]) => {
  const keep = keys.map((k) => [k, localStorage.getItem(k)] as const);
  localStorage.clear();
  keep.forEach(([k, v]) => v !== null && localStorage.setItem(k, v));
};

/**
 * A session that ended without a logout (token expired, access refused): write any Daily work still
 * being typed, then clear storage but keep this phone's unsaved Daily work copies. They are kept per
 * studio and user, so only the same person sees them after signing in again. A real logout asks first
 * and clears them (clearKeepingDevice).
 */
export const clearAfterExpiry = (keepDevice = false) => {
  flushAllDrafts();
  clearStorageKeeping([...(keepDevice ? DEVICE_KEYS : []), ...draftKeys()]);
};

/** Remember whether the owner last worked in Karkhana or the rest of Studio, to open there after the next login. */
export const rememberArea = (path: string) => {
  try {
    localStorage.setItem("hkp.lastArea", path.startsWith("/karkhana") ? "karkhana" : "studio");
  } catch {
    // Storage blocked: login simply opens the dashboard.
  }
};

/** Where an owner lands after logging in: Karkhana if that's where they last worked on this phone. */
export const homeAfterLogin = (role?: string): string => {
  let last: string | null = null;
  try {
    last = localStorage.getItem("hkp.lastArea");
  } catch {
    last = null;
  }
  return last === "karkhana" && isKarkhanaEnabled() && isStudioStaff(role) ? "/karkhana" : "/dashboard";
};
